import React, { useState, useEffect } from 'react';
import { adminAPI, Payment, PaymentHistoryEntry } from '../../services/api';
import './PaymentManagement.css';

interface PaymentStats {
  totalPending: number;
  pendingAmount: number;
  totalCompleted: number;
  completedAmount: number;
  outstandingBalance: number;
  activeLoanCount: number;
  overdueCount: number;
  overdueAmount: number;
  oldestOverdueDays: number;
  successRateThisMonth: number | null;
}

const emptyStats = (): PaymentStats => ({
  totalPending: 0,
  pendingAmount: 0,
  totalCompleted: 0,
  completedAmount: 0,
  outstandingBalance: 0,
  activeLoanCount: 0,
  overdueCount: 0,
  overdueAmount: 0,
  oldestOverdueDays: 0,
  successRateThisMonth: null,
});

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const calculatePaymentStats = (list: Payment[]): PaymentStats => {
  const today = startOfToday();
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const pending = list.filter((p) => p.status === 'pending');
  const completed = list.filter((p) => p.status === 'completed');
  const overdue = pending.filter((p) => new Date(p.dueDate) < today);

  const pendingAmount = pending.reduce((sum, p) => sum + p.amount, 0);
  const completedAmount = completed.reduce((sum, p) => sum + p.amount, 0);
  const overdueAmount = overdue.reduce((sum, p) => sum + p.amount, 0);

  const activeLoanIds = new Set(pending.map((p) => p.loanId));

  let oldestOverdueDays = 0;
  if (overdue.length > 0) {
    const oldestDue = Math.min(...overdue.map((p) => new Date(p.dueDate).getTime()));
    oldestOverdueDays = Math.max(
      0,
      Math.floor((today.getTime() - oldestDue) / (1000 * 60 * 60 * 24))
    );
  }

  const completedThisMonth = completed.filter(
    (p) => new Date(p.paidDate || p.updatedAt || p.createdAt) >= monthStart
  ).length;
  const failedThisMonth = list.filter(
    (p) =>
      p.status === 'failed' &&
      new Date(p.updatedAt || p.createdAt) >= monthStart
  ).length;
  const decidedThisMonth = completedThisMonth + failedThisMonth;
  const successRateThisMonth =
    decidedThisMonth > 0
      ? Math.round((completedThisMonth / decidedThisMonth) * 100)
      : completed.length > 0 && list.filter((p) => p.status === 'failed').length === 0
        ? 100
        : null;

  return {
    totalPending: pending.length,
    pendingAmount,
    totalCompleted: completed.length,
    completedAmount,
    outstandingBalance: pendingAmount,
    activeLoanCount: activeLoanIds.size,
    overdueCount: overdue.length,
    overdueAmount,
    oldestOverdueDays,
    successRateThisMonth,
  };
};

const PaymentManagement: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'dueDate' | 'amount' | 'borrower'>('dueDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [paymentsNeedingRetry, setPaymentsNeedingRetry] = useState<Payment[]>([]);
  const [showRetrySection, setShowRetrySection] = useState(false);
  const [stats, setStats] = useState<PaymentStats>(emptyStats);
  const [loading, setLoading] = useState(true);

  // Fetch payments from API (unfiltered — table filters client-side so cards stay accurate)
  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getPayments({
        page: 1,
        limit: 100
      });
      
      if (response.success) {
        setPayments(response.data.payments);
        setStats(calculatePaymentStats(response.data.payments));
      } else {
        console.error('Failed to fetch payments:', response.message);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchPaymentsForRetry();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchPayments();
      fetchPaymentsForRetry();
    }, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch payments needing retry
  const fetchPaymentsForRetry = async () => {
    try {
      const response = await adminAPI.getPaymentsForRetry();
      if (response.success) {
        setPaymentsNeedingRetry(response.data.payments);
      }
    } catch (error) {
      console.error('Error fetching payments for retry:', error);
    }
  };

  // Fetch payment history for selected payment
  const fetchPaymentHistory = async (loanId: string) => {
    try {
      setLoadingHistory(true);
      const response = await adminAPI.getLoanPaymentHistory(loanId);
      if (response.success) {
        setPaymentHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      setPaymentHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const filteredPayments = payments
    .filter(payment => {
      const matchesStatus = filterStatus === 'all' || payment.status === filterStatus;
      const matchesSearch = payment.borrowerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           payment.id.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesSearch;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'dueDate':
          comparison = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'borrower':
          comparison = a.borrowerName.localeCompare(b.borrowerName);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const getStatusColor = (status: Payment['status']) => {
    switch (status) {
      case 'pending':
        return '#EA580C'; // Amber
      case 'completed':
        return '#2ECC40'; // Green
      case 'failed':
        return '#FF4136'; // Red
      case 'cancelled':
        return '#6b7280'; // Gray
      case 'refunded':
        return '#8b5cf6'; // Purple
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status: Payment['status']) => {
    switch (status) {
      case 'pending':
        return (
          <svg className="status-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12,6 12,12 16,14"/>
          </svg>
        );
      case 'completed':
        return (
          <svg className="status-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22,4 12,14.01 9,11.01"/>
          </svg>
        );
      case 'failed':
        return (
          <svg className="status-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        );
      case 'cancelled':
        return (
          <svg className="status-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
          </svg>
        );
      case 'refunded':
        return (
          <svg className="status-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s-10-6-10-12V5l10-3 10 3v5c0 6-10 12-10 12z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const formatCurrency = (amount: number) => {
    // Use compact format for better display in cards
    const formatted = new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
    
    // Replace spaces with non-breaking spaces to prevent line breaks
    return formatted.replace(/\s/g, '\u00A0');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handlePaymentAction = async (paymentId: string, action: 'mark_completed' | 'view_details' | 'retry') => {
    const payment = payments.find(p => p.id === paymentId);
    if (payment) {
      setSelectedPayment(payment);
      setShowPaymentModal(true);
      // Fetch payment history when viewing details
      if (action === 'view_details') {
        await fetchPaymentHistory(payment.loanId);
      }
    }
  };

  const handleRetryPayment = async (paymentId: string, failureReason?: string) => {
    try {
      const response = await adminAPI.retryFailedPayment(paymentId, failureReason);
      if (response.success) {
        alert(response.message);
        // Refresh payments
        const paymentsResponse = await adminAPI.getPayments({ page: 1, limit: 100 });
        if (paymentsResponse.success) {
          setPayments(paymentsResponse.data.payments);
        }
        await fetchPaymentsForRetry();
        setShowPaymentModal(false);
        setSelectedPayment(null);
      }
    } catch (error) {
      console.error('Error retrying payment:', error);
      alert('Failed to retry payment. Please try again.');
    }
  };

  const markAsCompleted = async (paymentId: string, paymentMethod: string, reference: string, notes?: string) => {
    try {
      // Call the API to update payment status
      const response = await adminAPI.updatePaymentStatus(paymentId, {
        status: 'completed',
        paymentMethod,
        reference,
        notes
      });
      
      if (response.success) {
        // Refresh payments
        const paymentsResponse = await adminAPI.getPayments({ page: 1, limit: 100 });
        if (paymentsResponse.success) {
          setPayments(paymentsResponse.data.payments);
          setStats(calculatePaymentStats(paymentsResponse.data.payments));
        }
        
        setShowPaymentModal(false);
        setSelectedPayment(null);
        setPaymentHistory([]);
      } else {
        console.error('Failed to update payment:', response.message);
        alert('Failed to update payment: ' + response.message);
      }
      
    } catch (error: any) {
      console.error('Error marking payment as completed:', error);
      alert('Failed to update payment: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return (
      <div className="payment-management">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading payment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-management">
      <div className="payment-header">
        <h1>Payment Management</h1>
        <p>Track loan repayments, overdue accounts, and payment history.</p>
      </div>

      {/* Payment Statistics */}
      <div className="payment-stats">
        <div className="stat-card stat-card--pending">
          <div className="stat-label">Pending Payments</div>
          <div className="stat-number">{stats.totalPending}</div>
          <div className="stat-meta">{formatCurrency(stats.pendingAmount)} due</div>
          {stats.totalPending > 0 && (
            <div className="stat-footer">
              <span className="stat-pill stat-pill--pending">
                <span className="stat-pill-dot" aria-hidden="true" />
                Needs attention
              </span>
            </div>
          )}
        </div>

        <div className="stat-card stat-card--completed">
          <div className="stat-label">Completed</div>
          <div className="stat-number">{stats.totalCompleted}</div>
          <div className="stat-meta">{formatCurrency(stats.completedAmount)} collected</div>
          {stats.successRateThisMonth !== null && (
            <div className="stat-footer">
              <span className="stat-pill stat-pill--completed">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
                {stats.successRateThisMonth}% success this month
              </span>
            </div>
          )}
        </div>

        <div className="stat-card stat-card--outstanding">
          <div className="stat-label">Outstanding Balance</div>
          <div className="stat-number">{formatCurrency(stats.outstandingBalance)}</div>
          <div className="stat-meta">
            Across {stats.activeLoanCount} active loan{stats.activeLoanCount === 1 ? '' : 's'}
          </div>
        </div>

        <div className="stat-card stat-card--overdue">
          <div className="stat-label">Overdue Accounts</div>
          <div className="stat-number">{stats.overdueCount}</div>
          <div className="stat-meta">{formatCurrency(stats.overdueAmount)} overdue</div>
          {stats.overdueCount > 0 && (
            <div className="stat-footer">
              <span className="stat-pill stat-pill--overdue">
                <span className="stat-pill-dot" aria-hidden="true" />
                Oldest: {stats.oldestOverdueDays} day{stats.oldestOverdueDays === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="payment-filters">
        <div className="filter-group">
          <label htmlFor="status-filter">Status:</label>
          <select 
            id="status-filter"
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Payments</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="sort-by">Sort by:</label>
          <select 
            id="sort-by"
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value as 'dueDate' | 'amount' | 'borrower')}
            className="filter-select"
          >
            <option value="dueDate">Due Date</option>
            <option value="amount">Amount</option>
            <option value="borrower">Borrower</option>
          </select>
        </div>
        
        <div className="filter-group">
          <button 
            className="sort-order-btn"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
        
        <div className="search-group">
          <input
            type="text"
            placeholder="Search by borrower or payment ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Payments Table */}
      <div className="payments-table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>Borrower</th>
              <th>Amount</th>
              <th>Due Date</th>
              <th>Status</th>
              <th>Payment Method</th>
              <th>Reference</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.map((payment) => (
              <tr key={payment.id} className="payment-row">
                <td>
                  <div className="borrower-info">
                    <div className="borrower-name">{payment.borrowerName}</div>
                    <div className="payment-id" title={payment.id}>
                      {payment.id.length > 12 ? `${payment.id.slice(0, 8)}…` : payment.id}
                    </div>
                  </div>
                </td>
                
                <td className="amount-cell">
                  <span className="payment-amount">{formatCurrency(payment.amount)}</span>
                </td>
                
                <td className="due-date-cell">
                  <span className={`due-date ${payment.status === 'failed' ? 'failed' : ''}`}>
                    {formatDate(payment.dueDate)}
                  </span>
                </td>
                
                <td>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(payment.status) }}
                  >
                    {getStatusIcon(payment.status)} {payment.status.toUpperCase()}
                  </span>
                </td>
                
                <td>
                  <span className="payment-method">
                    {payment.paymentMethod ? (
                      <>
                        {payment.paymentMethod === 'payfast' && (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                              <line x1="1" y1="10" x2="23" y2="10"/>
                            </svg>
                            PayFast
                          </>
                        )}
                        {payment.paymentMethod === 'manual' && (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14,2 14,8 20,8"/>
                              <line x1="16" y1="13" x2="8" y2="13"/>
                              <line x1="16" y1="17" x2="8" y2="17"/>
                              <polyline points="10,9 9,9 8,9"/>
                            </svg>
                            Manual
                          </>
                        )}
                        {payment.paymentMethod === 'debit_order' && (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                              <line x1="8" y1="21" x2="16" y2="21"/>
                              <line x1="12" y1="17" x2="12" y2="21"/>
                            </svg>
                            Debit Order
                          </>
                        )}
                      </>
                    ) : (
                      <span className="no-method">-</span>
                    )}
                  </span>
                </td>
                
                <td>
                  <span className="payment-reference">
                    {payment.reference || <span className="no-reference">-</span>}
                  </span>
                </td>
                
                <td>
                  <div className="payment-actions">
                    {payment.status === 'pending' && (
                      <button 
                        className="action-btn mark-completed-btn"
                        onClick={() => handlePaymentAction(payment.id, 'mark_completed')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <polyline points="22,4 12,14.01 9,11.01"/>
                        </svg>
                        Mark Completed
                      </button>
                    )}
                    
                    {payment.status === 'failed' && (
                      <>
                        <button 
                          className="action-btn retry-btn"
                          onClick={() => handleRetryPayment(payment.id, payment.failureReason)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="23,4 23,10 17,10"/>
                            <polyline points="1,20 1,14 7,14"/>
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                          </svg>
                          Retry
                        </button>
                        <button 
                          className="action-btn mark-completed-btn"
                          onClick={() => handlePaymentAction(payment.id, 'mark_completed')}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22,4 12,14.01 9,11.01"/>
                          </svg>
                          Mark Completed
                        </button>
                      </>
                    )}
                    
                    <button 
                      className="action-btn view-btn"
                      onClick={() => handlePaymentAction(payment.id, 'view_details')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      View Details
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payments Needing Retry Section */}
      {paymentsNeedingRetry.length > 0 && (
        <div className="retry-section-container">
          <div className="retry-section-header">
            <h2>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23,4 23,10 17,10"/>
                <polyline points="1,20 1,14 7,14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              Payments Needing Retry ({paymentsNeedingRetry.length})
            </h2>
            <button 
              className="toggle-retry-btn"
              onClick={() => setShowRetrySection(!showRetrySection)}
            >
              {showRetrySection ? 'Hide' : 'Show'}
            </button>
          </div>
          {showRetrySection && (
            <div className="retry-payments-list">
              {paymentsNeedingRetry.map((payment) => (
                <div key={payment.id} className="retry-payment-item">
                  <div className="retry-payment-info">
                    <div className="retry-payment-borrower">{payment.borrowerName}</div>
                    <div className="retry-payment-amount">{formatCurrency(payment.amount)}</div>
                    {payment.failureReason && (
                      <div className="retry-payment-reason">Reason: {payment.failureReason}</div>
                    )}
                  </div>
                  <div className="retry-payment-actions">
                    <button 
                      className="action-btn retry-btn"
                      onClick={() => handleRetryPayment(payment.id, payment.failureReason)}
                    >
                      Retry
                    </button>
                    <button 
                      className="action-btn view-btn"
                      onClick={() => handlePaymentAction(payment.id, 'view_details')}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Payment Details Modal */}
      {showPaymentModal && selectedPayment && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Payment Details</h2>
              <button 
                className="modal-close"
                onClick={() => setShowPaymentModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="payment-details">
                {/* Loan Summary — the most useful context for admin */}
                {selectedPayment.loanSummary && (
                  <div className="detail-section" style={{ background: '#f0f9ff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #bae6fd' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <h3 style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Loan {selectedPayment.loanNumber || ''}</h3>
                      <span
                        style={{
                          fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase',
                          padding: '0.2rem 0.6rem', borderRadius: 999,
                          background: selectedPayment.loanSummary.loanStatus === 'repaid' ? '#d1fae5' : '#dbeafe',
                          color: selectedPayment.loanSummary.loanStatus === 'repaid' ? '#065f46' : '#1e40af',
                        }}
                      >
                        {selectedPayment.loanSummary.loanStatus}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', textAlign: 'center' }}>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Total Owed</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e3a5f' }}>{formatCurrency(selectedPayment.loanSummary.totalAmount)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Paid So Far</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#059669' }}>{formatCurrency(selectedPayment.loanSummary.totalPaid)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Outstanding</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: selectedPayment.loanSummary.outstandingBalance > 0 ? '#dc2626' : '#059669' }}>
                          {formatCurrency(Math.max(0, selectedPayment.loanSummary.outstandingBalance))}
                        </div>
                      </div>
                    </div>
                    {selectedPayment.loanSummary.installmentTotal > 1 && (
                      <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                        <div style={{
                          background: '#e0f2fe', borderRadius: 6, padding: '0.4rem 0.75rem',
                          fontSize: '0.8rem', color: '#0369a1', fontWeight: 600, display: 'inline-block'
                        }}>
                          Installment {selectedPayment.loanSummary.installmentCompleted + 1} of {selectedPayment.loanSummary.installmentTotal}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="detail-section">
                  <h3>💳 This Payment</h3>
                  <div className="detail-grid">
                    <div className="detail-item full-width">
                      <span className="detail-label">Installment Amount:</span>
                      <span className="detail-value amount large">{formatCurrency(selectedPayment.amount)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span
                        className="detail-value status-badge"
                        style={{
                          backgroundColor: getStatusColor(selectedPayment.status),
                          color: 'white', padding: '0.25rem 0.75rem',
                          borderRadius: '999px', fontSize: '0.8rem'
                        }}
                      >
                        {getStatusIcon(selectedPayment.status)} {selectedPayment.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Due Date:</span>
                      <span className="detail-value">{selectedPayment.dueDate ? formatDate(selectedPayment.dueDate) : 'N/A'}</span>
                    </div>
                    {selectedPayment.status === 'completed' && selectedPayment.paidDate && (
                      <div className="detail-item">
                        <span className="detail-label">Paid Date:</span>
                        <span className="detail-value success">{formatDate(selectedPayment.paidDate)}</span>
                      </div>
                    )}
                    {selectedPayment.paymentMethod && (
                      <div className="detail-item">
                        <span className="detail-label">Method:</span>
                        <span className="detail-value">{selectedPayment.paymentMethod.toUpperCase()}</span>
                      </div>
                    )}
                    {selectedPayment.reference && (
                      <div className="detail-item">
                        <span className="detail-label">Reference:</span>
                        <span className="detail-value code">{selectedPayment.reference}</span>
                      </div>
                    )}
                    {selectedPayment.failureReason && (
                      <div className="detail-item full-width">
                        <span className="detail-label">⚠️ Failure Reason:</span>
                        <span className="detail-value failure-reason">{selectedPayment.failureReason}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="detail-section">
                  <h3>👤 Student</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Name:</span>
                      <span className="detail-value">{selectedPayment.borrowerName}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{selectedPayment.borrowerEmail || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{selectedPayment.borrowerPhone || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Payment History Section */}
                <div className="payment-history-section">
                  <h3>Payment History</h3>
                  {loadingHistory ? (
                    <div className="loading-state">
                      <div className="loading-spinner-small"></div>
                      <p>Loading payment history...</p>
                    </div>
                  ) : paymentHistory.length > 0 ? (
                    <div className="history-list">
                      {paymentHistory.map((entry) => (
                        <div key={entry.id} className="history-item">
                          <div className="history-status">
                            <span 
                              className="history-status-badge"
                              style={{ backgroundColor: getStatusColor(entry.status as Payment['status']) }}
                            >
                              {entry.status.toUpperCase()}
                            </span>
                            <span className="history-date">{formatDate(entry.status_changed_at)}</span>
                          </div>
                          {entry.notes && (
                            <div className="history-notes">{entry.notes}</div>
                          )}
                          {entry.failure_reason && (
                            <div className="history-failure-reason">Reason: {entry.failure_reason}</div>
                          )}
                          {entry.changed_by && (
                            <div className="history-changed-by">Changed by: {entry.changed_by}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-history">No payment history available</p>
                  )}
                </div>
                
                {selectedPayment.status === 'pending' && (
                  <div className="admin-actions-section">
                    <h3>📧 Student Notifications</h3>
                    <p>Send a payment reminder to the student.</p>
                    <div className="form-actions">
                      <button 
                        type="button"
                        className="reminder-btn"
                        onClick={async () => {
                          try {
                            const result = await adminAPI.sendPaymentReminder(selectedPayment.id, 'sms');
                            if (result.success) {
                              alert(`✅ SMS reminder sent to ${selectedPayment.borrowerName}!`);
                            } else {
                              alert(`❌ Failed to send SMS: ${result.message}`);
                            }
                          } catch (error: any) {
                            console.error('Error sending SMS reminder:', error);
                            alert(`Failed to send SMS reminder: ${error.message || 'Unknown error'}`);
                          }
                        }}
                      >
                        📱 Send SMS Reminder
                      </button>
                      <button 
                        type="button"
                        className="reminder-btn email"
                        onClick={async () => {
                          try {
                            const result = await adminAPI.sendPaymentReminder(selectedPayment.id, 'email');
                            if (result.success) {
                              alert(`✅ Email reminder sent to ${selectedPayment.borrowerName}!`);
                            } else {
                              alert(`❌ Failed to send email: ${result.message}`);
                            }
                          } catch (error: any) {
                            console.error('Error sending email reminder:', error);
                            alert(`Failed to send email reminder: ${error.message || 'Unknown error'}`);
                          }
                        }}
                      >
                        ✉️ Send Email Reminder
                      </button>
                      <button 
                        type="button"
                        className="reminder-btn both"
                        onClick={async () => {
                          try {
                            const result = await adminAPI.sendPaymentReminder(selectedPayment.id, 'both');
                            if (result.success) {
                              alert(`✅ SMS & Email reminders sent to ${selectedPayment.borrowerName}!`);
                            } else {
                              alert(`❌ Failed: ${result.message}`);
                            }
                          } catch (error: any) {
                            console.error('Error sending reminders:', error);
                            alert(`Failed to send reminders: ${error.message || 'Unknown error'}`);
                          }
                        }}
                      >
                        📨 Send Both
                      </button>
                    </div>
                    <p className="info-note">
                      💡 The student can pay via their dashboard at <strong>Dashboard → Payments</strong>
                    </p>
                  </div>
                )}

                {(selectedPayment.status === 'pending' || selectedPayment.status === 'failed') && (
                  <div className="mark-completed-section">
                    <h3>Mark as Completed</h3>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target as HTMLFormElement);
                      const paymentMethod = formData.get('paymentMethod') as string;
                      const reference = formData.get('reference') as string;
                      const notes = formData.get('notes') as string;
                      markAsCompleted(selectedPayment.id, paymentMethod, reference, notes);
                    }}>
                      <div className="form-group">
                        <label htmlFor="paymentMethod">Payment Method:</label>
                        <select id="paymentMethod" name="paymentMethod" required>
                          <option value="">Select method</option>
                          <option value="payfast">PayFast</option>
                          <option value="manual">Manual</option>
                          <option value="debit_order">Debit Order</option>
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="reference">Reference/Transaction ID:</label>
                        <input 
                          type="text" 
                          id="reference" 
                          name="reference" 
                          placeholder="Enter transaction reference"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="notes">Notes (optional):</label>
                        <textarea 
                          id="notes" 
                          name="notes" 
                          placeholder="Add any additional notes"
                          rows={3}
                        />
                      </div>
                      
                      <div className="form-actions">
                        <button type="submit" className="submit-btn">
                          ✅ Mark as Completed
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {selectedPayment.status === 'failed' && (
                  <div className="retry-section">
                    <h3>Retry Payment</h3>
                    <p>This payment has failed. You can retry it or mark it as completed manually.</p>
                    <div className="form-actions">
                      <button 
                        type="button"
                        className="retry-btn"
                        onClick={() => handleRetryPayment(selectedPayment.id, selectedPayment.failureReason)}
                      >
                        🔄 Retry Payment
                      </button>
                    </div>
                  </div>
                )}

                {/* Reset to Pending — dev/testing only */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;
